import React, { useState } from "react";
import { ClipboardList, PackageSearch, ScrollText } from "lucide-react";
import InventoryView from "./InventoryView";
import AssignInventoryModal from "./AssignInventoryModal";
import AssetTagSearch from "./AssetTagSearch";
import UnitDetailModal from "./UnitDetailModal";
import IssuedView from "./IssuedView";
import RevokeAssignmentModal from "./RevokeAssignmentModal";
import AddItemModal from "./AddItemModal";
import useUnits from "./useUnits";
import useIssued from "./useIssued";
import useItems from "./useItems";
import useCatalog from "./useCatalog";
import { Stat } from "./shared";
import { AssignPayload, AttributeValue, EntityOption, EntityType, InventoryRow, IssuedRow, NewItemPayload, UnitStatus } from "./types";
import { Container, Header, HeaderRow, StatsRow, Tab, TabsNav } from "./styles";
import { notify } from "../../../utils";
import Layout from "../../../components/custom/Layout";
import useEmployees from "../../dev/Inventory/useEmployees";
import useUser from "../../../customHooks/useUser";
import AddQuantityModal from "./AddQuantityModal";
import LogView from "./LogView";
import SerialsModal, { useSerialsModalDataStore } from "./SerialsModal";

type InventoryTab = "inventory" | "issued" | "log";

export default function Inventory() {
    const { user } = useUser();
    const { departments, itemTypes, definitions } = useCatalog();
    const { items, getItems, addItem, assignItem: postAssign } = useItems();

    const [tab, setTab] = useState<InventoryTab>("inventory");

    const defsForType = (typeId: number) => definitions.filter((d) => d.itemTypeId === typeId);
    const typeById = (id: number) => itemTypes.find((t) => t.id === id);
    const deptById = (id: number) => departments.find((d) => d.id === id);
    const deptForType = (typeId: number) => {
        const type = typeById(typeId);
        return type ? deptById(type.departmentId) : undefined;
    };

    const totalQty = items.reduce((s, i) => s + i.availableQuantity, 0);

    const [assignItemId, setAssignItemId] = useState<string | null>(null);
    const assignItem = items.find((i) => i.id == assignItemId) || null;

    const handleAssign = async ({ quantity, entityType, entityId, entityName, fromUnit }: AssignPayload) => {
        if (!assignItem) return;
        const res = await postAssign(assignItem.id, fromUnit, entityType, entityId, quantity);
        if ("assigned" in res) {
            notify(`Assigned ${res.assigned} × ${assignItem.name} to ${entityName}`);
        } else {
            notify(`Transferred ${res.transferred} × ${assignItem.name} from ${res.from} to ${res.to}`);
        }
        setAssignItemId(null);
        getItems();
        getIssued();
    };

    const [addItemModal, setAddItemModal] = useState(false);

    const handleAddItem = async (payload: NewItemPayload) => {
        try {
            await addItem(payload);
            notify("Item added to inventory");
            setAddItemModal(false);
            getItems();
            refreshSerials();
        } catch (err) {
            notify("Couldn't add that item.", "error");
        }
    };

    const { result, unit, setUnit, loadUnit, assignUnit, revokeUnit, setUnitStatus, patchUnit, searchAssetTag } = useUnits();
    const { issued, getIssued, revoke } = useIssued(user.s_unit);

    const [revokeRow, setRevokeRow] = useState<IssuedRow | null>(null);
    const [addQtyModal, setAddQtyModal] = useState(false);
    const refreshSerials = useSerialsModalDataStore(s => s.refreshSerials);

    const handleRevoke = async (quantity: number) => {
        if (!revokeRow) return;
        try {
            await revoke(revokeRow.itemId, revokeRow.employeeId, quantity, user.s_unit);
            notify(`Revoked ${quantity} × ${revokeRow.itemName} from ${revokeRow.name}`);
            setRevokeRow(null);
            getIssued();
            getItems();
            refreshSerials();
        } catch (err) {
            notify("Couldn't revoke that assignment.", "error");
        }
    };

    const handleUnitAssign = async (entityType: EntityType, entityId: string) => {
        if (!unit) return;
        try {
            await assignUnit(unit.serial, entityType, entityId);
            if (entityType === 'employee') {
                await loadUnit(unit.serial);
            }
            refreshSerials();
            notify(`Assigned ${unit.serial}`);
        } catch (err) {
            notify("Couldn't assign that unit.", "error");
        }
    };

    const handleUnitRevoke = async () => {
        if (!unit) return;
        try {
            await revokeUnit(unit.serial);
            await loadUnit(unit.serial);
            refreshSerials();
            notify(`Revoked ${unit.serial}`);
        } catch (err) {
            notify("Couldn't revoke that unit.", "error");
        }
    };

    const handleUnitStatusChange = async (toStatus: UnitStatus, reasonCategory?: string, note?: string, keepHolder?: boolean) => {
        if (!unit) return;
        try {
            await setUnitStatus(unit.serial, toStatus, reasonCategory, note, keepHolder);
            await loadUnit(unit.serial);
            refreshSerials();
            notify(`${unit.serial} marked ${toStatus.replace("_", " ")}`);
        } catch (err) {
            notify("Couldn't update that unit.", "error");
        }
    };

    const handleUnitPatch = async (assetTag: string | null, attributes: Record<string, AttributeValue>) => {
        if (!unit) return;
        // No try/catch here — UnitDetailModal awaits this and handles the asset-tag
        // 409 inline vs. a generic save failure itself, so let errors propagate .
        await patchUnit(unit.serial, { assetTag, attributes });
        await loadUnit(unit.serial);
        refreshSerials();
        notify("Saved");
    };

    const handleAddQty = (id: string) => {
        setAssignItemId(id);
        setAddQtyModal(true);
    }

    return (
        <Layout>
            <Container>
                <Header>
                    <HeaderRow>
                        <h1>Inventory</h1>
                        <StatsRow>
                            <Stat label="Items" value={items.length} />
                            <Stat label="Units" value={totalQty} />
                            <Stat label="Types" value={itemTypes.length} />
                        </StatsRow>
                    </HeaderRow>

                    <HeaderRow>
                        <TabsNav>
                            <Tab active={tab === "inventory"} onClick={() => setTab("inventory")}>
                                <PackageSearch size={16} />
                                Manage
                            </Tab>
                            <Tab active={tab === "issued"} onClick={() => setTab("issued")}>
                                <ClipboardList size={16} />
                                Issued
                            </Tab>
                            <Tab active={tab === "log"} onClick={() => setTab("log")}>
                                <ScrollText size={16} />
                                Log
                            </Tab>
                        </TabsNav>
                        <AssetTagSearch result={result} onSearch={searchAssetTag} onSelect={loadUnit} />
                    </HeaderRow>
                </Header>

                {tab === "inventory" && (
                    <InventoryView
                        items={items}
                        departments={departments}
                        itemTypes={itemTypes}
                        defsForType={defsForType}
                        typeById={typeById}
                        deptForType={deptForType}
                        onRowClick={(row: InventoryRow) => setAssignItemId(row.id)}
                        onAddItem={() => setAddItemModal(true)}
                        handleAddQty={handleAddQty}
                    />
                )}

                {tab === "issued" && <IssuedView issued={issued} onRowClick={setRevokeRow} />}

                {tab === "log" && <LogView loadUnit={loadUnit} />}
            </Container>

            {/* <AssignInventoryModal
                modal={!!assignItem}
                setModal={(open) => !open && setAssignItemId(null)}
                item={assignItem}
                employees={employees}
                units={units}
                defaultUnit={user.s_unit}
                onAssign={handleAssign}
                setAddQtyModal={setAddQtyModal}
            /> */}

            <UnitDetailModal
                modal={!!unit}
                setModal={(open) => !open && setUnit(null)}
                unit={unit}
                onAssign={handleUnitAssign}
                onRevoke={handleUnitRevoke}
                onSetStatus={handleUnitStatusChange}
                onPatch={handleUnitPatch}
            />

            <RevokeAssignmentModal
                modal={!!revokeRow}
                setModal={(open) => !open && setRevokeRow(null)}
                row={revokeRow}
                onRevoke={handleRevoke}
            />

            <AddItemModal
                modal={addItemModal}
                setModal={setAddItemModal}
                departments={departments}
                itemTypes={itemTypes}
                defsForType={defsForType}
                onAdd={handleAddItem}
            />

            <SerialsModal
                loadUnit={loadUnit}
                handleAddQty={handleAddQty}
            />

            <AddQuantityModal
                modal={addQtyModal}
                setModal={setAddQtyModal}
                item={assignItem}
                getItems={getItems}
                refreshSerials={refreshSerials}
            />

        </Layout>
    );
}
